import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  Grid,
  InputBase,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CampaignIcon from "@mui/icons-material/Campaign";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import { getCampaignsApi, deleteCampaignApi } from "../../api/campaign.api";
import { toast } from "../../utils/toast";
import CampaignActionModal from "./components/CampaignActionModal";
import CampaignCard from "./components/CampaignCard";

export default function CampaignList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const pollingRef = useRef(null);

  useEffect(() => {
    fetchCampaigns();
    return () => clearInterval(pollingRef.current);
  }, []);

  // Auto-poll every 3s while any campaign is "sending"
  useEffect(() => {
    const hasSending = campaigns.some((c) => c.status === "sending");
    clearInterval(pollingRef.current);
    if (hasSending) {
      pollingRef.current = setInterval(silentRefresh, 3000);
    }
    return () => clearInterval(pollingRef.current);
  }, [campaigns]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await getCampaignsApi();
      setCampaigns(res.data?.data || []);
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh without loading spinner
  const silentRefresh = async () => {
    try {
      const res = await getCampaignsApi();
      setCampaigns(res.data?.data || []);
    } catch {
      // silent fail
    }
  };

  const handleDelete = async (id, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await deleteCampaignApi(id);
      setCampaigns((prev) => prev.filter((c) => c._id !== id));
    } catch {
      toast.error("Failed to delete campaign");
    }
  };

  const [actionModal, setActionModal] = useState({ open: false, campaignId: null });

  const handleOpenAction = (id, e) => {
    if (e) e.stopPropagation();
    setActionModal({ open: true, campaignId: id });
  };

  const handleCloseAction = () => {
    setActionModal({ open: false, campaignId: null });
  };

  const handleEdit = (id) => {
    handleCloseAction();
    navigate(`/campaign/edit/${id}`);
  };

  const filtered = campaigns.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: theme.palette.primary.main,
              color: "#fff",
            }}
          >
            <CampaignIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "text.primary" }}>
              Campaigns
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Manage and monitor your WhatsApp campaigns efficiently.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "background.default",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              minWidth: 180,
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
            <InputBase
              placeholder="Search campaigns..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ fontSize: 13 }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/campaign/create")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              px: 2.5,
              whiteSpace: "nowrap",
            }}
          >
            New Campaign
          </Button>
        </Stack>
      </Stack>

      {/* Campaign List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : filtered.length === 0 && campaigns.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 3,
            borderStyle: "dashed",
          }}
        >
          <CampaignIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#475569", mb: 0.5 }}>
            No campaigns yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#94a3b8", mb: 2 }}>
            Create your first WhatsApp campaign to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/campaign/create")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Create Campaign
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {filtered.map((c) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={c._id}>
              <CampaignCard
                campaign={c}
                onClick={(e) => handleOpenAction(c._id, e)}
                onEdit={handleEdit}
                onDelete={(id) => handleDelete(id, { stopPropagation: () => {} })}
              />
            </Grid>
          ))}

          {/* Create Campaign Card */}
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Paper
              elevation={0}
              onClick={() => navigate("/campaign/create")}
              sx={{
                height: "100%",
                minHeight: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                borderRadius: 3,
                border: "2px dashed",
                borderColor: "divider",
                bgcolor: "transparent",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  bgcolor: `${theme.palette.primary.main}08`,
                  "& .create-icon": { bgcolor: theme.palette.primary.main, color: "#fff" },
                },
              }}
            >
              <Box
                className="create-icon"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "action.hover",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                }}
              >
                <AddIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.secondary" }}>
                Create Campaign
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Campaign Action Modal */}
      <CampaignActionModal
        open={actionModal.open}
        onClose={handleCloseAction}
        campaignId={actionModal.campaignId}
        onRefresh={fetchCampaigns}
        onEdit={handleEdit}
      />
    </Box>
  );
}
